import apiWrapper from 'Helpers/apiWrapper'
import extractApiData from 'Helpers/extractApiData'
import { encodeParams } from 'mailup-react-helpers'

const endpoints = (baseURL => {
  return {
    checkImages: (idMessage, timeout) =>
      `${baseURL}/api/message/${idMessage}/check/images?${encodeParams({
        timeout
      })}`,
    fastMessageCheck: idMessage => `${baseURL}/api/message/${idMessage}/check`,
    checkSpam: idMessage => `${baseURL}/api/message/${idMessage}/spam/check`,
    imageCheck: () => `${baseURL}/api/image/check`,
    linkCheck: () => `${baseURL}/api/link/check`,
    EOACheck: idMessage =>
      `${baseURL}/api/message/${idMessage}/emailonacid/spam/check`,
    getEOAClients: `${baseURL}/api/messages/preview/clients`,
    getMessagePreview: (idMessage) => `${baseURL}/api/message/${idMessage}/preview`,
    retryGetMessagePreview: (idMessage) => `${baseURL}/api/message/${idMessage}/preview/retry`
  }
})(process.env.BEEMAILCHECKUP_URL)

/**
 * @typedef {Object} CheckImagesResponse
 * @property {boolean} Value
 *
 * @description check any images linked to the specific message passed, if timeout is available check within that timeout
 * @param {number} idMessage The message id
 * @param {number} timeout add a timeout to the call
 * @return {CheckImagesResponse}
 */
export const beEmailCheckupCheckImages = (idMessage, timeout = 0) => {
  return apiWrapper({
    method: 'get',
    url: endpoints.checkImages(idMessage, timeout)
  }).then(extractApiData)
}

/**
 * @typedef {Object} CheckupImageCheck
 * @property {boolean} Value
 *
 * @description check an image url, if timeout is available check within that timeout
 * @param {object} {Url: string} The image url
 * @param {number} timeout add a timeout to the call
 * @return {CheckupImageCheck}
 */
export const beEmailCheckupImageCheck = data => {
  return apiWrapper({
    method: 'post',
    url: endpoints.imageCheck(),
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  }).then(extractApiData)
}

/**
<<<<<<< HEAD
<<<<<<< HEAD
=======
>>>>>>> d536e41b (wip: add link check endpoint)
 * @typedef {Object} CheckuplinkCheck
 * @property {boolean} Value
 *
 * @description check an link url, if timeout is available check within that timeout
 * @param {object} {Url: string} The link url
 * @param {number} timeout add a timeout to the call
 * @return {CheckupLinkCheck}
 */
export const beEmailCheckupLinkCheck = data => {
  return apiWrapper({
    method: 'post',
    url: endpoints.linkCheck(),
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  }).then(extractApiData)
}

/**
<<<<<<< HEAD
=======
>>>>>>> 72e88a9c (wip: update table, add checkup image service)
=======
>>>>>>> d536e41b (wip: add link check endpoint)
 *
 * @typedef {Object} CheckAttachments
 * @property {Array<string>} FileName
 * @property {boolean} Check
 *
 * @typedef {Object} CheckSize message size in kbytes
 * @property {number} Size
 * @property {boolean} Check
 *
 *
 * @typedef {Object} CheckDynamicContents
 * @property {Array<string>} ContentName
 * @property {boolean} Check
 *
 *
 * @typedef {Object} CheckDynamicTag
 * @property {string} Value
 * @property {('Body'|'Subject'|'Preheader ')} Location
 *
 *
 * @typedef {Object} CheckDynamicFields
 * @property {boolean} Check
 * @property {boolean} IsDynamicFieldsEnabled
 * @property {boolean} ContainsDynamicFields
 * @property {Array<CheckDynamicTag>} WrongDynamicTags
 *
 *
 * @typedef {Object} CheckMaxSize if CheckMaxSize > 0, check is failed. it provides number of kbytes over the maximum value allowed
 * @property {number} Size
 * @property {number} OverMaxSize
 * @property {boolean} Check
 *
 *
 * @typedef {Object} CheckLinkResult
 * @property {string} Domain
 * @property {Array<string>} Urls
 * @property {number} Action
 * @property {string} Code
 *
 *
 * @typedef {Object} BlackListResult
 * @property {boolean} SomeDomainsInBlackList
 * @property {number} Action issues level: Ignore = 0, Warning = 1, Error = 2
 * @property {Array<CheckLinkResult>} CheckDetail
 *
 * @typedef {Object} FastMessageCheckResponse the plane message check rapresentation
 * @property {CheckSize} CheckSize
 * @property {CheckAttachments} CheckAttachments
 * @property {CheckDynamicContents} CheckDynamicContents
 * @property {boolean} CheckLinks
 * @property {boolean} CheckImages
 * @property {boolean} CheckLinksWoTracking
 * @property {boolean} CheckUnsubscribeLink
 * @property {CheckDynamicFields} CheckFields
 * @property {boolean} CheckPreheader
 * @property {CheckMaxSize} CheckMaxSize
 * @property {boolean} CheckWebAnalytics
 * @property {boolean} CheckLiquidCode
 * @property {BlackListResult} CheckBlacklistResult
 * @property {CheckContent} CheckContent
 *
 */

/**
 * @description Fast Message Check - check validity of any contents linked to the specific message passed
 *
 * @param {number} idMessage
 *
 * @param {Object} data
 * @property {array} Checks
 *
 * @property Size - Checks message size
 * @property MaxSize - Checks maximum message size
 * @property Attachments - Checks message attachments
 * @property DynamicContents - Checks any message dynamic contents
 * @property LinksWoTracking - Checks if there are any links without tracking into the message
 * @property UnsubscribeLink - Checks if there are any unsubscribe links into the message
 * @property Fields - Checks any message custom fields
 * @property Preheader - Checks message pre header
 * @property WebAnalytics - Checks message web analytics
 * @property LiquidCode - Checks any personalization linked to a message
 * @property Links - Checks any message links reputation
 * @property Images - Checks any message images reachability
 *
 * @returns {FastMessageCheckResponse}
 */
export const beEmailCheckupFastMessageCheck = (idMessage, data) => {
  return apiWrapper({
    method: 'post',
    url: endpoints.fastMessageCheck(idMessage),
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  }).then(extractApiData)
}

/**
 * @typedef {Object} SpamCheckResponse
 * @property {boolean} IsAvailable
 * @property {string} Report
 * @property {string} Score
 * @property {string} Threshold
 * @property {string} IsSpam
 *
 * @description Check Spam - check if message could be checked as spam by spamassassin service
 * @param {number} idMessage
 * @returns {SpamCheckResponse}
 */
export const beEmailCheckupCheckSpam = idMessage => {
  return apiWrapper({
    method: 'post',
    url: endpoints.checkSpam(idMessage),
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(extractApiData)
}

/**
 * @typedef {Object} EOASpamCheckResponseDetails
 * @property {string} Score
 * @property {string} Feedback
 * @property {string} FeedbackType
 *
 *
 * @typedef {Object} EOASpamCheckResponse
 * @property {string} Provider
 * @property {string} Type
 * @property {boolean} IsSpam
 * @property {EOASpamCheckResponseDetails} Details
 *
 * @description check if message could be checked as spam by EmailOnAcid service
 * @param {number} idMessage
 * @returns {Array<EOASpamCheckResponseDetails>}
 */
export const beEmailCheckupEOACheck = idMessage => {
  return apiWrapper({
    method: 'post',
    url: endpoints.EOACheck(idMessage),
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(extractApiData)
}

/**
 * @typedef {Object} EOAGetClientsResponse
 * @property {string} Id
 * @property {string} Category
 * @property {string} DisplayName
 * @property {string} OperativeStystem
 * @property {string} Browser
 *
 * @description Get email message preview processed by EmailOnAcid service
 * @returns {Array<EOAGetClientsResponse>}
 */
export const beEmailCheckupGetEOAClients = () => {
  return apiWrapper({
    method: 'get',
    url: endpoints.getEOAClients,
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(extractApiData)
}

/**
 * @typedef {Object} EOAPreviewMessage
 * @property {string} Id
 * @property {string} Category
 * @property {string} DisplayName
 * @property {string} OperativeStystem
 * @property {string} Browser
 *
 * @description Get email message preview processed by EmailOnAcid service
 * @param {number} idMessage
 * @param {Object} data {TestType: string}
 * @returns {Array<EOAPreviewMessage>}
 */
export const beEmailCheckupGetMessagePreviews = (idMessage, data) => {
  return apiWrapper({
    method: 'post',
    url: endpoints.getMessagePreview(idMessage),
    data: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(extractApiData)
}

/**
 * @typedef {Object} EOAPreviewMessage
 * @property {boolean} Result
 *
 * @description Get email message preview processed by EmailOnAcid service
 * @params {number} idMessage
 * @returns {Array<EOAPreviewMessage>}
 */
export const beEmailCheckupRetryGetMessagePreview = (idMessage, data) => {
  return apiWrapper({
    method: 'post',
    url: endpoints.retryGetMessagePreview(idMessage),
    data: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(extractApiData)
}
